import React, {useEffect, useState} from 'react';
import {
    Alert,
    App,
    Button,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Pagination,
    Select,
    Spin,
    Steps,
    Switch,
    Table,
    Tooltip
} from 'antd';
import {
    AppstoreAddOutlined,
    AppstoreOutlined,
    ArrowLeftOutlined,
    CheckOutlined,
    CloseOutlined,
    CopyOutlined,
    EditOutlined,
    EyeOutlined,
    PlayCircleOutlined,
    RightOutlined,
    RocketOutlined
} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
    CreateMyStrategyRequest,
    MyStrategyItem,
    MyStrategyListResponse,
    myStrategyService,
    StrategyCodeValidateResponse
} from '../../services/myStrategyService';
import {apiConnectionService} from '../../services/apiConnectionService';
import {FreqtradeConfig, freqtradeConfigService} from '../../services/freqtradeConfigService';
import {strategyExecutionService} from '../../services/strategyExecutionService';
import {strategyTaskService} from '../../services/strategyTaskService';
import type {ApiConnectionModel} from '../../types/apiConnection';
import styles from './MyStrategyPage.module.css';

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'running' | 'follow' | 'original';

// 策略卡片数据（模拟/扩展字段）
interface StrategyCardData {
    id: number;
    strategyConfigId?: number;
    name: string;
    description: string;
    version: string;
    type: string;
    pair: string;
    timeframe: string;
    status: 'live' | 'sim' | 'draft';
    isRunning: boolean;
    sourceType: 'FOLLOW' | 'ORIGINAL';
    monthlyReturn: number;
    maxDrawdown: number;
    sharpeRatio: number;
    runningDays: number;
    totalSignals: number;
    aiInsight: string;
    readiness?: number; // 实盘就绪度（仅模拟盘）
    entryLogic?: string;
    exitLogic?: string;
    position?: string;
}

interface CreateStrategyFormValues extends CreateMyStrategyRequest {
    title: string;
    strategyName: string;
}

interface LaunchTaskFormValues {
    apiKeyId: number;
    freqtradeConfigId: number;
    tradingPair: string;
    initialCapital: number;
    stopLossPercent: number;
    takeProfitPercent: number;
}

// 示例策略代码数据
const EXAMPLE_STRATEGIES = [
    {
        name: '趋势跟踪策略',
        code: `# 趋势跟踪策略 - 双均线交叉
import numpy as np
from freqtrade.strategy import IStrategy
from pandas import DataFrame

class TrendFollowingStrategy(IStrategy):
    # 策略参数
    minimal_roi = {"0": 0.10}  # 10%利润退出
    stoploss = -0.05  # 5%止损
    timeframe = '1h'

    # 均线周期
    fast_ma_period = 10
    slow_ma_period = 30

    def populate_indicators(self, dataframe: DataFrame) -> DataFrame:
        # 计算快慢均线
        dataframe['fast_ma'] = dataframe['close'].rolling(
            window=self.fast_ma_period).mean()
        dataframe['slow_ma'] = dataframe['close'].rolling(
            window=self.slow_ma_period).mean()
        return dataframe

    def populate_buy_trend(self, dataframe: DataFrame) -> DataFrame:
        # 快线上穿慢线买入
        dataframe.loc[
            (dataframe['fast_ma'] > dataframe['slow_ma']) &
            (dataframe['fast_ma'].shift(1) <= dataframe['slow_ma'].shift(1)),
            'buy'
        ] = 1
        return dataframe

    def populate_sell_trend(self, dataframe: DataFrame) -> DataFrame:
        # 快线下穿慢线卖出
        dataframe.loc[
            (dataframe['fast_ma'] < dataframe['slow_ma']) &
            (dataframe['fast_ma'].shift(1) >= dataframe['slow_ma'].shift(1)),
            'sell'
        ] = 1
        return dataframe`
    },
    {
        name: 'RSI反转策略',
        code: `# RSI反转策略 - 超买超卖反转
import talib
from freqtrade.strategy import IStrategy
from pandas import DataFrame

class RSIReversalStrategy(IStrategy):
    minimal_roi = {"0": 0.08}
    stoploss = -0.03
    timeframe = '4h'

    # RSI参数
    rsi_period = 14
    rsi_buy_threshold = 30  # 超卖区域
    rsi_sell_threshold = 70  # 超买区域

    def populate_indicators(self, dataframe: DataFrame) -> DataFrame:
        # 计算RSI指标
        dataframe['rsi'] = talib.RSI(
            dataframe['close'],
            timeperiod=self.rsi_period
        )
        return dataframe

    def populate_buy_trend(self, dataframe: DataFrame) -> DataFrame:
        # RSI从超卖区域反弹买入
        dataframe.loc[
            (dataframe['rsi'] < self.rsi_buy_threshold) &
            (dataframe['rsi'].shift(1) >= self.rsi_buy_threshold),
            'buy'
        ] = 1
        return dataframe

    def populate_sell_trend(self, dataframe: DataFrame) -> DataFrame:
        # RSI从超买区域回落卖出
        dataframe.loc[
            (dataframe['rsi'] > self.rsi_sell_threshold) &
            (dataframe['rsi'].shift(1) <= self.rsi_sell_threshold),
            'sell'
        ] = 1
        return dataframe`
    },
    {
        name: '布林带突破策略',
        code: `# 布林带突破策略
import talib
from freqtrade.strategy import IStrategy
from pandas import DataFrame

class BollingerBandsStrategy(IStrategy):
    minimal_roi = {"0": 0.12}
    stoploss = -0.04
    timeframe = '1h'

    # 布林带参数
    bb_period = 20
    bb_std = 2.0

    def populate_indicators(self, dataframe: DataFrame) -> DataFrame:
        # 计算布林带
        bollinger = talib.BBANDS(
            dataframe['close'],
            timeperiod=self.bb_period,
            nbdevup=self.bb_std,
            nbdevdn=self.bb_std
        )
        dataframe['bb_upper'] = bollinger[0]
        dataframe['bb_middle'] = bollinger[1]
        dataframe['bb_lower'] = bollinger[2]
        return dataframe

    def populate_buy_trend(self, dataframe: DataFrame) -> DataFrame:
        # 价格突破下轨后反弹买入
        dataframe.loc[
            (dataframe['close'] > dataframe['bb_lower']) &
            (dataframe['close'].shift(1) <= dataframe['bb_lower'].shift(1)),
            'buy'
        ] = 1
        return dataframe

    def populate_sell_trend(self, dataframe: DataFrame) -> DataFrame:
        # 价格突破上轨后回落卖出
        dataframe.loc[
            (dataframe['close'] < dataframe['bb_upper']) &
            (dataframe['close'].shift(1) >= dataframe['bb_upper'].shift(1)),
            'sell'
        ] = 1
        return dataframe`
    }
];

const MyStrategyPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [createForm] = Form.useForm<CreateStrategyFormValues>();
    const [taskForm] = Form.useForm<LaunchTaskFormValues>();
    const [myStrategyData, setMyStrategyData] = useState<MyStrategyListResponse>({
        list: [],
        total: 0,
        page: 0,
        size: 12,
        totalPages: 0
    });
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [filter, setFilter] = useState<FilterType>('all');
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [runningInstanceMap, setRunningInstanceMap] = useState<Record<number, number>>({});
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createStep, setCreateStep] = useState(0);
    const [creating, setCreating] = useState(false);
    const [showExample, setShowExample] = useState(false);
    const [selectedExample, setSelectedExample] = useState(0);
    const [editingStrategy, setEditingStrategy] = useState<MyStrategyItem | null>(null);
    // 策略代码验证状态
    const [validating, setValidating] = useState(false);
    const [validateResult, setValidateResult] = useState<StrategyCodeValidateResponse | null>(null);
    const [codeValidated, setCodeValidated] = useState(false);
    const [taskModalVisible, setTaskModalVisible] = useState(false);
    const [launchingTask, setLaunchingTask] = useState(false);
    const [taskRunMode, setTaskRunMode] = useState<'LIVE' | 'DRY_RUN'>('DRY_RUN');
    const [selectedTaskStrategy, setSelectedTaskStrategy] = useState<MyStrategyItem | null>(null);
    const [apiConnections, setApiConnections] = useState<ApiConnectionModel[]>([]);
    const [freqtradeConfigs, setFreqtradeConfigs] = useState<FreqtradeConfig[]>([]);
    const highlightStrategyId = Number(searchParams.get('highlightId') || 0);
    const fromVisual = searchParams.get('from') === 'visual';

    // 当编辑弹窗打开时，获取详情并回填表单数据
    useEffect(() => {
        if (createModalVisible && editingStrategy) {
            // 根据ID获取完整策略详情
            myStrategyService.getMyStrategyById(editingStrategy.id).then(detail => {
                // 从 description 中提取版本号
                const versionMatch = detail.description?.match(/版本:\s*(.+?)(?:\n|$)/);
                const version = versionMatch ? versionMatch[1] : '';
                // 去除版本信息的 description
                const descWithoutVersion = detail.description?.replace(/版本:\s*.+?(?:\n|$)/, '').trim() || '';

                createForm.setFieldsValue({
                    title: detail.title,
                    strategyName: detail.strategyName || '',
                    version: version,
                    description: descWithoutVersion,
                    strategyCode: detail.strategyCode || '',
                    prompt: detail.prompt || '',
                    isActive: detail.isActive === 1
                });
                setCreateStep(0);
            }).catch(err => {
                console.error('获取策略详情失败', err);
                messageApi.error('获取策略详情失败');
            });
        }
    }, [createModalVisible, editingStrategy]);

    useEffect(() => {
        loadMyStrategies(0);
        loadTaskLaunchDependencies();
    }, [filter]);

    useEffect(() => {
        if (!highlightStrategyId || myStrategyData.list.length === 0) {
            return;
        }
        const timer = window.setTimeout(() => {
            const row = document.querySelector(`[data-row-key="${highlightStrategyId}"]`);
            if (row instanceof HTMLElement) {
                row.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
        }, 120);

        return () => window.clearTimeout(timer);
    }, [highlightStrategyId, myStrategyData.list]);

    // 点击外部关闭菜单
    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const loadMyStrategies = async (page: number) => {
        try {
            const data = await myStrategyService.getMyStrategies(page, 12, filter);
            setMyStrategyData(data);
        } catch (error) {
            console.error('加载我的策略失败', error);
        }
    };

    const loadTaskLaunchDependencies = async () => {
        try {
            const [connections, configs] = await Promise.all([
                apiConnectionService.getAllConnections(),
                freqtradeConfigService.getActiveConfigs()
            ]);
            setApiConnections(connections.filter(connection => connection.id && connection.status === 'active'));
            setFreqtradeConfigs(configs || []);
        } catch (error) {
            console.error('加载任务启动依赖失败', error);
        }
    };

    const handlePageChange = (page: number) => {
        loadMyStrategies(page - 1);
    };

    const openTaskModal = (strategy: StrategyCardData, runMode: 'LIVE' | 'DRY_RUN') => {
        const originalItem = myStrategyData.list.find(item => item.id === strategy.id);
        if (!originalItem) {
            messageApi.error('策略数据不存在');
            return;
        }
        if (!originalItem.strategyConfigId) {
            messageApi.error('该策略尚未绑定 StrategyConfig，暂时无法创建运行任务');
            return;
        }

        const defaultConnection = apiConnections.find(connection => connection.isDefault) || apiConnections[0];
        const defaultFreqtrade = freqtradeConfigs.find(config => config.isDefault === 1) || freqtradeConfigs[0];

        setSelectedTaskStrategy(originalItem);
        setTaskRunMode(runMode);
        taskForm.resetFields();
        taskForm.setFieldsValue({
            apiKeyId: defaultConnection?.id,
            freqtradeConfigId: defaultFreqtrade?.id,
            tradingPair: 'BTC/USDT',
            initialCapital: 10000,
            stopLossPercent: -8,
            takeProfitPercent: 18
        });
        setTaskModalVisible(true);
    };

    const closeTaskModal = () => {
        if (launchingTask) {
            return;
        }
        setTaskModalVisible(false);
        setSelectedTaskStrategy(null);
        taskForm.resetFields();
    };

    const submitTaskLaunch = async () => {
        if (!selectedTaskStrategy) {
            return;
        }

        try {
            const values = await taskForm.validateFields();
            setLaunchingTask(true);

            const created = await strategyTaskService.createTask({
                taskName: `${selectedTaskStrategy.title}-${taskRunMode === 'LIVE' ? '实盘' : '模拟盘'}`,
                taskType: 'FREQTRADE',
                exchangeId: values.apiKeyId,
                myStrategyId: selectedTaskStrategy.id,
                strategyConfigId: selectedTaskStrategy.strategyConfigId!,
                apiKeyId: values.apiKeyId,
                freqtradeConfigId: values.freqtradeConfigId,
                runMode: taskRunMode,
                tradingPair: values.tradingPair,
                stopLossPercent: values.stopLossPercent,
                takeProfitPercent: values.takeProfitPercent,
                initialCapital: values.initialCapital,
                autoStart: true
            });

            messageApi.success(`已创建并启动${taskRunMode === 'LIVE' ? '实盘' : '模拟盘'}任务`);
            closeTaskModal();
            await Promise.all([loadMyStrategies(0), loadRunningInstanceMap()]);
            if (created?.freqtradeInstanceId) {
                navigate(`/strategy-execution/monitor/${created.freqtradeInstanceId}`);
                return;
            }
            navigate('/strategy/tasks');
        } catch (error: any) {
            if (error && typeof error === 'object' && 'errorFields' in error) {
                return;
            }
            messageApi.error(error?.message || '启动任务失败');
        } finally {
            setLaunchingTask(false);
        }
    };

    const handleDeleteStrategy = async (id: number) => {
        try {
            await myStrategyService.deleteMyStrategy(id);
            messageApi.success('策略删除成功');
            await loadMyStrategies(myStrategyData.page);
        } catch (error) {
            console.error('删除策略失败', error);
            messageApi.error('删除策略失败');
        }
    };

    const openCreateModal = () => {
        setEditingStrategy(null);
        createForm.resetFields();
        createForm.setFieldsValue({
            isActive: true
        });
        setCreateStep(0);
        setValidating(false);
        setValidateResult(null);
        setCodeValidated(false);
        setCreateModalVisible(true);
    };

    const openEditModal = (strategy: StrategyCardData) => {
        const originalItem = myStrategyData.list.find(s => s.id === strategy.id);
        if (!originalItem) return;

        setEditingStrategy(originalItem);
        createForm.resetFields();
        setCreateStep(0);
        setValidating(false);
        setValidateResult(null);
        setCodeValidated(false);
        setCreateModalVisible(true);
    };

    const handleOpenVisualEditor = (strategy: StrategyCardData) => {
        const originalItem = myStrategyData.list.find(item => item.id === strategy.id);
        if (!originalItem) {
            messageApi.error('未找到策略详情');
            return;
        }
        if (originalItem.sourceType === 'FOLLOW') {
            messageApi.warning('跟随策略暂不支持可视化编辑');
            return;
        }
        navigate(`/strategy/logic-flow/${strategy.id}`);
    };

    const handleCreateAndNavigateToEditor = async () => {
        try {
            const result = await myStrategyService.createMyStrategy({title: '未命名策略'});
            navigate(`/strategy/logic-flow/${result.id}`);
        } catch (e: any) {
            messageApi.error('创建策略失败: ' + (e.message || '未知错误'));
        }
    };

    const closeCreateModal = () => {
        if (creating) {
            return;
        }
        setCreateModalVisible(false);
        setCreateStep(0);
        setEditingStrategy(null);
        setValidating(false);
        setValidateResult(null);
        setCodeValidated(false);
    };

    // 判断当前是否正在编辑FOLLOW（复制）类型策略
    const isFollowEditing = editingStrategy?.sourceType === 'FOLLOW';

    const nextCreateStep = async () => {
        if (createStep === 0) {
            await createForm.validateFields(['title', 'strategyName']);
            // FOLLOW策略跳过"策略内容"步骤，直接进入"描述与状态"
            if (isFollowEditing) {
                setCreateStep(2);
                return;
            }
        }
        if (createStep === 1) {
            await createForm.validateFields(['prompt', 'strategyCode']);
            const values = createForm.getFieldsValue(true) as CreateStrategyFormValues;

            // 如果已验证通过，直接进入下一步
            if (codeValidated) {
                setCreateStep((prev) => prev + 1);
                return;
            }

            // 如果有策略代码，执行验证
            if (values.strategyCode && values.strategyCode.trim()) {
                setValidating(true);
                setValidateResult(null);
                try {
                    const result = await myStrategyService.validateStrategyCode({
                        strategyCode: values.strategyCode,
                        strategyName: values.strategyName
                    });
                    setValidateResult(result);

                    if (!result.valid) {
                        // 验证失败，重置状态
                        setCodeValidated(false);
                        return;
                    }
                    // 验证成功，设置已验证状态
                    setCodeValidated(true);
                } catch (error) {
                    console.error('策略代码验证失败', error);
                    messageApi.error('策略代码验证服务异常');
                    setCodeValidated(false);
                    return;
                } finally {
                    setValidating(false);
                }
            } else {
                // 没有策略代码，直接进入下一步
                setCreateStep((prev) => prev + 1);
            }
            return;
        }
        setCreateStep((prev) => prev + 1);
    };

    const prevCreateStep = () => {
        // FOLLOW策略从"描述与状态"退回直接到"基础信息"，跳过策略内容步骤
        if (isFollowEditing && createStep === 2) {
            setCreateStep(0);
            return;
        }
        setCreateStep((prev) => prev - 1);
    };

    const submitCreateStrategy = async () => {
        try {
            await createForm.validateFields(['description', 'isActive']);
            const values = createForm.getFieldsValue(true) as CreateStrategyFormValues;
            setCreating(true);

            if (editingStrategy) {
                await myStrategyService.updateMyStrategy(editingStrategy.id, values);
                messageApi.success('策略更新成功');
            } else {
                await myStrategyService.createMyStrategy(values);
                messageApi.success('策略创建成功');
            }

            setCreateModalVisible(false);
            setCreateStep(0);
            setEditingStrategy(null);
            await loadMyStrategies(0);
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in (error as any)) {
                return;
            }
            messageApi.error(editingStrategy ? '策略更新失败' : '策略创建失败');
        } finally {
            setCreating(false);
        }
    };

    const loadRunningInstanceMap = async () => {
        try {
            const runningInstances = await strategyExecutionService.getRunningStrategies();
            const nextMap = runningInstances.reduce<Record<number, number>>((acc, instance) => {
                if (typeof instance.strategyConfigId === 'number' && typeof instance.id === 'number') {
                    acc[instance.strategyConfigId] = instance.id;
                }
                return acc;
            }, {});
            setRunningInstanceMap(nextMap);
        } catch (error) {
            console.error('加载运行实例映射失败', error);
            setRunningInstanceMap({});
        }
    };

    const toggleMenu = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === id ? null : id);
    };

    // 从 description 中解析版本号
    const parseVersionFromDescription = (description?: string): string => {
        if (!description) return '-';
        // 匹配 "版本: xxx" 格式
        const match = description.match(/版本:\s*(.+?)(?:\n|$)/);
        return match ? match[1].trim() : '-';
    };

    // 将 MyStrategy 数据转换为卡片数据
    const convertToCardData = (strategy: MyStrategyItem): StrategyCardData => {
        const sourceType = strategy.sourceType === 'FOLLOW' ? 'FOLLOW' : 'ORIGINAL';
        const isRunning = strategy.isRunning === 1;
        const version = parseVersionFromDescription(strategy.description);

        return {
            id: strategy.id,
            strategyConfigId: strategy.strategyConfigId,
            name: strategy.title,
            description: strategy.description || '',
            version,
            type: sourceType === 'FOLLOW' ? 'FOLLOW' : 'ORIGINAL',
            pair: 'BTC/USDT', // 需要从后端获取
            timeframe: '日线', // 需要从后端获取
            status: sourceType === 'FOLLOW' ? 'sim' : 'live',
            isRunning,
            sourceType,
            monthlyReturn: (strategy.profit || 0) * 100,
            maxDrawdown: (strategy.maxDrawdown || 0) * 100,
            sharpeRatio: strategy.sharpeRatio || 0,
            runningDays: 0,
            totalSignals: 0,
            aiInsight: '策略运行正常，当前处于稳定盈利状态。',
            readiness: sourceType === 'FOLLOW' ? 43 : undefined,
            entryLogic: 'RSI < 30 且价格触碰布林带下轨',
            exitLogic: 'RSI > 65 或跌破 -10%',
            position: '每次 30% · 日线周期'
        };
    };

    const allStrategies: StrategyCardData[] = myStrategyData.list.map(convertToCardData);

    // 过滤策略
    const filteredStrategies = allStrategies.filter(s => {
        if (filter === 'all') return true;
        if (filter === 'running') return s.isRunning;
        if (filter === 'follow') return s.sourceType === 'FOLLOW';
        return s.sourceType === 'ORIGINAL';
    });

    const runningCount = allStrategies.filter(s => s.isRunning).length;
    const originalCount = allStrategies.filter(s => s.sourceType === 'ORIGINAL').length;
    const followCount = allStrategies.filter(s => s.sourceType === 'FOLLOW').length;
    const firstRunningId = allStrategies.find(s => s.isRunning)?.strategyConfigId;

    const handleGoToMonitor = async (strategyConfigId?: number) => {
        if (!strategyConfigId) {
            navigate('/system/strategy-execution');
            return;
        }

        const mappedInstanceId = runningInstanceMap[strategyConfigId];
        if (mappedInstanceId) {
            navigate(`/strategy-execution/monitor/${mappedInstanceId}`);
            return;
        }

        try {
            const runningInstances = await strategyExecutionService.getRunningStrategies();
            const refreshedMap = runningInstances.reduce<Record<number, number>>((acc, instance) => {
                if (typeof instance.strategyConfigId === 'number' && typeof instance.id === 'number') {
                    acc[instance.strategyConfigId] = instance.id;
                }
                return acc;
            }, {});
            setRunningInstanceMap(refreshedMap);
            const refreshedInstanceId = refreshedMap[strategyConfigId];
            if (refreshedInstanceId) {
                navigate(`/strategy-execution/monitor/${refreshedInstanceId}`);
                return;
            }
        } catch (error) {
            console.error('刷新运行实例映射失败', error);
        }

        navigate('/system/strategy-execution');
    };

    // 渲染迷你收益曲线
    const renderMiniChart = (status: string) => {
        const color = status === 'live' ? 'var(--pt-bull)' : status === 'sim' ? 'var(--pt-primary)' : 'var(--pt-text-dim)';
        return (
            <div className={styles.miniChart}>
                <svg width="100%" height="52" viewBox="0 0 260 52" preserveAspectRatio="none">
                    <defs>
                        <linearGradient id={`grad-${status}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity="0.25"/>
                            <stop offset="100%" stopColor={color} stopOpacity="0"/>
                        </linearGradient>
                    </defs>
                    <path
                        d="M0,45 C30,42 50,38 70,32 C90,26 110,20 130,16 C150,12 170,10 190,8 C210,6 230,4 260,2 L260,52 L0,52 Z"
                        fill={`url(#grad-${status})`}
                    />
                    <polyline
                        points="0,45 30,42 50,38 70,32 90,26 110,20 130,16 150,12 170,10 190,8 210,6 230,4 260,2"
                        fill="none"
                        stroke={color}
                        strokeWidth="1.5"
                    />
                    <circle cx="220" cy="5" r="3" fill={color}/>
                </svg>
            </div>
        );
    };

    // 渲染策略卡片
    const renderStrategyCard = (strategy: StrategyCardData) => {
        const statusConfig = {
            live: {color: 'var(--pt-bull)', label: '原创', bg: 'var(--pt-bull-dim)'},
            sim: {color: 'var(--pt-primary)', label: '跟随策略', bg: 'var(--pt-primary-dim)'},
            draft: {color: 'var(--pt-text-dim)', label: '草稿 · 未回测', bg: 'var(--pt-bg-hover)'}
        };
        const config = statusConfig[strategy.status];

        return (
            <div
                key={strategy.id}
                className={`${styles.strategyCard} ${styles[strategy.status]}`}
                data-status={strategy.status}
            >
                <div className={styles.statusBar} style={{background: config.color}}/>
                <div className={styles.cardContent}>
                    {/* 顶部 */}
                    <div className={styles.cardHeader}>
                        <div>
                            <div className={styles.statusBadge}>
                                <span className={styles.statusDot} style={{background: config.color}}/>
                                <span style={{color: config.color}}>{config.label}</span>
                                {strategy.isRunning && (
                                    <button
                                        className={styles.runningBadge}
                                        onClick={() => handleGoToMonitor(strategy.strategyConfigId)}
                                        title="查看运行监控"
                                    >
                                        运行中
                                    </button>
                                )}
                            </div>
                            <div className={styles.strategyName}>{strategy.name}</div>
                            <div
                                className={styles.strategyMeta}>{strategy.type} · {strategy.pair} {strategy.timeframe}</div>
                        </div>
                        <div className={styles.menuWrapper}>
                            <button
                                className={styles.menuBtn}
                                onClick={(e) => toggleMenu(e, `menu-${strategy.id}`)}
                            >
                                ⋯
                            </button>
                            {openMenuId === `menu-${strategy.id}` && (
                                <div className={styles.dropdownMenu}>
                                    {strategy.status === 'live' && (
                                        <>
                                            <div onClick={() => {
                                            }}>查看实盘
                                            </div>
                                            <div onClick={() => {
                                            }}>重新回测
                                            </div>
                                            <div onClick={() => {
                                            }}>编辑策略
                                            </div>
                                            <div onClick={() => {
                                            }}>加入对比
                                            </div>
                                            <div className={styles.divider}/>
                                            <div className={styles.danger} onClick={() => {
                                            }}>暂停策略
                                            </div>
                                        </>
                                    )}
                                    {strategy.status === 'sim' && (
                                        <>
                                            <div onClick={() => {
                                            }}>查看模拟盘
                                            </div>
                                            <div onClick={() => {
                                            }}>重新回测
                                            </div>
                                            <div onClick={() => {
                                            }}>编辑策略
                                            </div>
                                            <div className={styles.divider}/>
                                            <div className={styles.primary} onClick={() => {
                                            }}>转实盘 →
                                            </div>
                                        </>
                                    )}
                                    {strategy.status === 'draft' && (
                                        <>
                                            <div onClick={() => {
                                            }}>编辑策略
                                            </div>
                                            <div className={styles.primary} onClick={() => {
                                            }}>开始回测
                                            </div>
                                            <div className={styles.divider}/>
                                            <div className={styles.danger} onClick={() => {
                                            }}>删除草稿
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 收益曲线 */}
                    {strategy.status !== 'draft' ? (
                        renderMiniChart(strategy.status)
                    ) : (
                        <div className={styles.chartPlaceholder}>
                            回测后显示收益曲线
                        </div>
                    )}

                    {/* 指标网格 */}
                    <div className={styles.metricsGrid}>
                        <div className={styles.metricBox}>
                            <div className={styles.metricLabel}>本月收益</div>
                            <div
                                className={`${styles.metricValue} ${strategy.monthlyReturn >= 0 ? styles.up : styles.down}`}>
                                {strategy.monthlyReturn >= 0 ? '+' : ''}{strategy.monthlyReturn.toFixed(1)}%
                            </div>
                        </div>
                        <div className={styles.metricBox}>
                            <div className={styles.metricLabel}>最大回撤</div>
                            <div className={`${styles.metricValue} ${styles.down}`}>
                                {strategy.maxDrawdown.toFixed(1)}%
                            </div>
                        </div>
                        <div className={styles.metricBox}>
                            <div
                                className={styles.metricLabel}>{strategy.status === 'draft' ? '策略逻辑' : '累计信号'}</div>
                            <div className={styles.metricValue}>
                                {strategy.status === 'draft' ? 'RSI' : `${strategy.totalSignals} 次`}
                            </div>
                        </div>
                    </div>

                    {/* AI 建议或策略逻辑 */}
                    {strategy.status === 'draft' ? (
                        <div className={styles.logicBox}>
                            <div><span className={styles.teal}>入场：</span>{strategy.entryLogic}</div>
                            <div><span className={styles.coral}>出场：</span>{strategy.exitLogic}</div>
                            <div><span className={styles.gold}>仓位：</span>{strategy.position}</div>
                        </div>
                    ) : (
                        <div className={styles.aiInsight}>
                            <strong>AI：</strong>
                            {strategy.sourceType === 'FOLLOW' ? '跟随策略已隐藏源码类字段。' : strategy.aiInsight}
                        </div>
                    )}

                    {/* 实盘就绪度（仅模拟盘） */}
                    {strategy.status === 'sim' && strategy.readiness !== undefined && (
                        <div className={styles.readinessBox}>
                            <div className={styles.readinessHeader}>
                                <span>实盘就绪度</span>
                                <span style={{color: 'var(--pt-primary)'}}>{strategy.readiness}%</span>
                            </div>
                            <div className={styles.progressBar}>
                                <div className={styles.progressFill} style={{width: `${strategy.readiness}%`}}/>
                            </div>
                            <div className={styles.readinessHint}>再运行 4 天达到开通条件</div>
                        </div>
                    )}

                    {/* 操作按钮 */}
                    <div className={styles.cardActions}>
                        {strategy.status === 'live' && (
                            <>
                                <Tooltip title={strategy.isRunning ? '查看实盘' : '启动实盘'}>
                                    <button
                                        className={`${styles.iconActionBtn} ${styles.iconActionTeal}`}
                                        onClick={() => strategy.isRunning ? handleGoToMonitor(strategy.strategyConfigId) : openTaskModal(strategy, 'LIVE')}
                                        aria-label={strategy.isRunning ? '查看实盘' : '启动实盘'}
                                    >
                                        {strategy.isRunning ? <EyeOutlined/> : <RocketOutlined/>}
                                    </button>
                                </Tooltip>
                                <Tooltip title="任务中心">
                                    <button className={styles.iconActionBtn}
                                            onClick={() => navigate('/strategy/tasks')}
                                            aria-label="任务中心">
                                        <AppstoreOutlined/>
                                    </button>
                                </Tooltip>
                            </>
                        )}
                        {strategy.status === 'sim' && (
                            <>
                                <Tooltip title={strategy.isRunning ? '查看模拟盘' : '启动模拟盘'}>
                                    <button
                                        className={`${styles.iconActionBtn} ${styles.iconActionGold}`}
                                        onClick={() => strategy.isRunning ? handleGoToMonitor(strategy.strategyConfigId) : openTaskModal(strategy, 'DRY_RUN')}
                                        aria-label={strategy.isRunning ? '查看模拟盘' : '启动模拟盘'}
                                    >
                                        {strategy.isRunning ? <EyeOutlined/> : <PlayCircleOutlined/>}
                                    </button>
                                </Tooltip>
                                <Tooltip title="任务中心">
                                    <button className={styles.iconActionBtn}
                                            onClick={() => navigate('/strategy/tasks')}
                                            aria-label="任务中心">
                                        <AppstoreOutlined/>
                                    </button>
                                </Tooltip>
                            </>
                        )}
                        {strategy.status === 'draft' && (
                            <>
                                <Tooltip title="开始回测">
                                    <button className={`${styles.iconActionBtn} ${styles.iconActionGold}`}
                                            aria-label="开始回测">
                                        <PlayCircleOutlined/>
                                    </button>
                                </Tooltip>
                                <Tooltip title="编辑">
                                    <button className={styles.iconActionBtn} onClick={() => openEditModal(strategy)}
                                            aria-label="编辑">
                                        <EditOutlined/>
                                    </button>
                                </Tooltip>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // 列表视图列定义
    const listColumns: ColumnsType<StrategyCardData> = [
        {
            title: '策略名称',
            key: 'name',
            render: (_, record) => (
                <div>
                    <div className={styles.listName}>{record.name}</div>
                    <div className={styles.listMeta}>{record.description}</div>
                </div>
            )
        },
        {
            title: '版本',
            dataIndex: 'version',
            key: 'version',
            align: 'center',
            width: 100,
            render: (value: string) => <span className={styles.mono}>{value || '-'}</span>
        },
        {
            title: '本月收益',
            dataIndex: 'monthlyReturn',
            key: 'monthlyReturn',
            align: 'right',
            render: (value: number) => {
                const num = typeof value === 'number' ? value : 0;
                return (
                    <span className={num >= 0 ? styles.up : styles.down}>
                        {num >= 0 ? '+' : ''}{num.toFixed(1)}%
                    </span>
                );
            }
        },
        {
            title: '最大回撤',
            dataIndex: 'maxDrawdown',
            key: 'maxDrawdown',
            align: 'right',
            render: (value: number) => {
                const num = typeof value === 'number' ? value : 0;
                return <span className={styles.down}>{num.toFixed(1)}%</span>;
            }
        },
        {
            title: '夏普比率',
            dataIndex: 'sharpeRatio',
            key: 'sharpeRatio',
            align: 'right',
            render: (value: number) => {
                const num = typeof value === 'number' ? value : 0;
                return <span>{num.toFixed(2)}</span>;
            }
        },
        {
            title: '操作',
            key: 'actions',
            align: 'right',
            width: 220,
            render: (_, record) => (
                <div className={styles.listActions}>
                    {'FOLLOW' !== record.sourceType && (
                        <Tooltip title="可视化编辑">
                            <button
                                type="button"
                                className={`${styles.iconBtn} ${styles.iconBtnAccent}`}
                                aria-label="可视化编辑"
                                onClick={() => handleOpenVisualEditor(record)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                                    <rect x="14" y="3" width="7" height="7" rx="1"/>
                                    <rect x="3" y="14" width="7" height="7" rx="1"/>
                                    <rect x="14" y="14" width="7" height="7" rx="1"/>
                                </svg>
                            </button>
                        </Tooltip>
                    )}
                    <Tooltip title="编辑">
                        <button
                            type="button"
                            className={styles.iconBtn}
                            aria-label="编辑"
                            onClick={() => openEditModal(record)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                        </button>
                    </Tooltip>
                    <Tooltip title="删除">
                        <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.danger}`}
                            aria-label="删除"
                            onClick={() => handleDeleteStrategy(record.id)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path
                                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                        </button>
                    </Tooltip>
                </div>
            )
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.scrollContainer}>
                {/* Header */}
                <div className={styles.dashHeader}>
                    <div>
                        <h2>我的策略</h2>
                        <p>
                            共 {allStrategies.length} 个策略 ·
                            {firstRunningId ? (
                                <button
                                    className={styles.runningSummaryBadge}
                                    onClick={() => handleGoToMonitor(firstRunningId)}
                                    title="跳转到运行监控"
                                >
                                    {runningCount} 个运行中
                                </button>
                            ) : (
                                <span className={styles.runningSummaryText}> {runningCount} 个运行中</span>
                            )}
                        </p>
                    </div>
                    <div className={styles.headerActions}>
                        <Tooltip title="可视化新建">
                            <button
                                className={styles.iconActionBtn}
                                onClick={handleCreateAndNavigateToEditor}
                                aria-label="可视化新建"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2">
                                    <path d="M12 5v14M5 12h14"/>
                                    <rect x="3" y="3" width="18" height="18" rx="4"/>
                                </svg>
                            </button>
                        </Tooltip>
                        {/* 视图切换 */}
                        <div className={styles.toggleGroup}>
                            <button
                                className={`${styles.toggleBtn} ${styles.disabled}`}
                                disabled
                                title="卡片视图（暂不支持）"
                            >
                                ⊞
                            </button>
                            <button
                                className={`${styles.toggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                                onClick={() => setViewMode('list')}
                                title="列表视图"
                            >
                                ☰
                            </button>
                        </div>
                        {/* 筛选 */}
                        <div className={styles.filterBar}>
                            {[
                                {key: 'all', label: '全部'},
                                {key: 'running', label: `运行中 (${runningCount})`},
                                {key: 'original', label: '原创'},
                                {key: 'follow', label: '跟随策略'}
                            ].map(f => (
                                <button
                                    key={f.key}
                                    className={`${styles.filterBtn} ${filter === f.key ? styles.active : ''}`}
                                    onClick={() => setFilter(f.key as FilterType)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <Tooltip title="新建策略">
                            <button className={`${styles.iconActionBtn} ${styles.iconActionGold}`}
                                    onClick={openCreateModal}
                                    aria-label="新建策略">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2">
                                    <path d="M12 5v14M5 12h14"/>
                                </svg>
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* 内容区域 */}
                <div className={styles.contentArea}>
                    {fromVisual && highlightStrategyId ? (
                        <div className={styles.returnBanner}>
                            <div>
                                <div className={styles.returnBannerTitle}>已从可视化编辑器返回</div>
                                <div className={styles.returnBannerText}>
                                    刚保存的策略已在列表中高亮显示，方便你继续查看或进入运行流程。
                                </div>
                            </div>
                            <div className={styles.returnBannerActions}>
                                <Tooltip title="继续可视化编辑">
                                    <button
                                        className={styles.iconActionBtn}
                                        onClick={() => navigate(`/strategy/logic-flow/${highlightStrategyId}`)}
                                        aria-label="继续可视化编辑"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6"/>
                                            <path d="M4 12h10"/>
                                        </svg>
                                    </button>
                                </Tooltip>
                                <Tooltip title="关闭提示">
                                    <button
                                        className={styles.iconActionBtn}
                                        onClick={() => setSearchParams({})}
                                        aria-label="关闭提示"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2">
                                            <path d="M18 6L6 18M6 6l12 12"/>
                                        </svg>
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    ) : null}

                    {/* Summary strip */}
                    <div className={styles.summaryStrip}>
                        <div className={styles.kpi} style={{borderLeftColor: 'var(--pt-bull)'}}>
                            <div className={styles.kpiLabel}>原创策略</div>
                            <div className={styles.kpiValue}>{originalCount}</div>
                            <div className={styles.kpiChange}>ORIGINAL {originalCount}</div>
                        </div>
                        <div className={styles.kpi} style={{borderLeftColor: 'var(--pt-primary)'}}>
                            <div className={styles.kpiLabel}>跟随策略</div>
                            <div className={styles.kpiValue}>{followCount}</div>
                            <div className={styles.kpiChange}>FOLLOW {followCount}</div>
                        </div>
                        <div className={styles.kpi} style={{borderLeftColor: 'var(--pt-purple)'}}>
                            <div className={styles.kpiLabel}>策略总数</div>
                            <div className={styles.kpiValue}>{allStrategies.length}</div>
                            <div className={styles.kpiChange}><span
                                style={{color: 'var(--pt-text-dim)'}}>原创 + 跟随</span>
                            </div>
                        </div>
                        <div className={styles.kpi} style={{borderLeftColor: 'var(--pt-blue)'}}>
                            <div className={styles.kpiLabel}>当前分页数量</div>
                            <div className={styles.kpiValue}>{allStrategies.length}</div>
                            <div className={styles.kpiChange}><span
                                style={{color: 'var(--pt-text-dim)'}}>每页 {myStrategyData.size} 条</span></div>
                        </div>
                    </div>

                    {/* 卡片视图 */}
                    {viewMode === 'grid' && (
                        <div className={styles.gridView}>
                            {filteredStrategies.map(renderStrategyCard)}
                            {/* 添加新策略卡片 */}
                            <div className={styles.addCard} onClick={openCreateModal}>
                                <div className={styles.addIcon}>+</div>
                                <div className={styles.addText}>创建新策略</div>
                                <div className={styles.addHint}>积木模式 · 向导模式 · AI 生成</div>
                            </div>
                        </div>
                    )}

                    {/* 列表视图 */}
                    {viewMode === 'list' && (
                        <div className={styles.listView}>
                            {filteredStrategies.length === 0 ? (
                                <Empty
                                    description="暂无策略"
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                >
                                    <Tooltip title="去可视化创建">
                                        <button
                                            type="button"
                                            className={`${styles.iconActionBtn} ${styles.iconActionGold}`}
                                            onClick={handleCreateAndNavigateToEditor}
                                            aria-label="去可视化创建"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" strokeWidth="2">
                                                <path d="M12 5v14M5 12h14"/>
                                                <rect x="3" y="3" width="18" height="18" rx="4"/>
                                            </svg>
                                        </button>
                                    </Tooltip>
                                </Empty>
                            ) : (
                                <div className={styles.listContainer}>
                                    <Table
                                        columns={listColumns}
                                        dataSource={filteredStrategies}
                                        rowKey="id"
                                        pagination={false}
                                        rowClassName={(record) => {
                                            const statusClass = styles[`row${record.status.charAt(0).toUpperCase() + record.status.slice(1)}`];
                                            const highlightClass = record.id === highlightStrategyId ? styles.rowHighlighted : '';
                                            return `${statusClass} ${highlightClass}`.trim();
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* 分页 */}
                    {viewMode === 'grid' && myStrategyData.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <Pagination
                                current={myStrategyData.page + 1}
                                pageSize={myStrategyData.size}
                                total={myStrategyData.total}
                                onChange={handlePageChange}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </div>
            </div>
            <Modal
                title={editingStrategy ? '编辑策略' : '创建新策略'}
                open={createModalVisible}
                onCancel={closeCreateModal}
                width={showExample ? 1100 : 820}
                footer={
                    <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                        <div>
                            {createStep > 0 && (
                                <Tooltip title="上一步">
                                    <Button shape="circle" icon={<ArrowLeftOutlined/>} onClick={prevCreateStep}
                                            disabled={creating} aria-label="上一步"/>
                                </Tooltip>
                            )}
                        </div>
                        <div style={{display: 'flex', gap: 8}}>
                            <Tooltip title="取消">
                                <Button shape="circle" icon={<CloseOutlined/>} onClick={closeCreateModal}
                                        disabled={creating} aria-label="取消"/>
                            </Tooltip>
                            {createStep < 2 ? (
                                <Tooltip
                                    title={createStep === 1 && !isFollowEditing && !codeValidated ? '校验代码' : '下一步'}>
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={createStep === 1 && !isFollowEditing && !codeValidated ?
                                            <CheckOutlined/> : <RightOutlined/>}
                                        onClick={nextCreateStep}
                                        disabled={creating || validating}
                                        loading={validating}
                                        aria-label={createStep === 1 && !isFollowEditing && !codeValidated ? '校验代码' : '下一步'}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip title={editingStrategy ? '更新策略' : '创建策略'}>
                                    <Button type="primary" shape="circle" icon={<CheckOutlined/>}
                                            onClick={submitCreateStrategy}
                                            loading={creating} aria-label={editingStrategy ? '更新策略' : '创建策略'}/>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                }
            >
                <Steps
                    current={isFollowEditing ? (createStep === 2 ? 1 : 0) : createStep}
                    items={isFollowEditing
                        ? [{title: '基础信息'}, {title: '描述与状态'}]
                        : [{title: '基础信息'}, {title: '策略内容'}, {title: '描述与状态'}]
                    }
                    style={{marginBottom: 20}}
                />
                <Form form={createForm} layout="vertical">
                    {createStep === 0 && (
                        <>
                            <Form.Item
                                name="title"
                                label="标题"
                                rules={[{required: true, message: '请输入标题'}]}
                            >
                                <Input placeholder="例如: 趋势跟踪策略"/>
                            </Form.Item>
                            <Form.Item
                                name="strategyName"
                                label="策略名称"
                                rules={[{required: true, message: '请输入策略名称'}]}
                            >
                                <Input
                                    placeholder="例如: TrendFollowing"
                                    disabled={isFollowEditing}
                                />
                            </Form.Item>
                            <Form.Item name="version" label="版本">
                                <Input placeholder="例如: v1.0.0"/>
                            </Form.Item>
                        </>
                    )}
                    {/* 策略内容步骤：仅ORIGINAL策略显示，FOLLOW策略不允许编辑源码 */}
                    {createStep === 1 && !isFollowEditing && (
                        <>
                            <Form.Item name="prompt" label="Prompt提示词">
                                <Input.TextArea rows={5} placeholder="策略的Prompt提示词"/>
                            </Form.Item>
                            <Form.Item label="策略代码">
                                <div className={styles.codeEditorWrapper}>
                                    <div className={styles.codeEditorMain}>
                                        <div className={styles.codeEditorHeader}>
                                            <span className={styles.codeLang}>Python</span>
                                            <Tooltip title={showExample ? '收起示范' : '示范代码'}>
                                                <button
                                                    type="button"
                                                    className={`${styles.iconActionBtn} ${showExample ? styles.iconActionGold : ''}`}
                                                    onClick={() => setShowExample(!showExample)}
                                                    aria-label={showExample ? '收起示范' : '示范代码'}
                                                >
                                                    <CopyOutlined/>
                                                </button>
                                            </Tooltip>
                                        </div>
                                        <Form.Item name="strategyCode" noStyle>
                                            <Input.TextArea
                                                rows={12}
                                                placeholder="# 在此输入Python策略代码&#10;from freqtrade.strategy import IStrategy"
                                                className={styles.codeTextarea}
                                                spellCheck={false}
                                                onChange={() => {
                                                    // 代码变化时重置验证状态
                                                    setCodeValidated(false);
                                                    setValidateResult(null);
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                    {showExample && (
                                        <div className={styles.codeExamplePanel}>
                                            <div className={styles.exampleTabs}>
                                                {EXAMPLE_STRATEGIES.map((ex, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={`${styles.exampleTab} ${selectedExample === idx ? styles.active : ''}`}
                                                        onClick={() => setSelectedExample(idx)}
                                                    >
                                                        {ex.name}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className={styles.exampleCodeWrapper}>
                                                <div className={styles.exampleCodeHeader}>
                                                    <span>{EXAMPLE_STRATEGIES[selectedExample].name}</span>
                                                    <Tooltip title="使用此代码">
                                                        <button
                                                            type="button"
                                                            className={`${styles.iconActionBtn} ${styles.iconActionTeal}`}
                                                            onClick={() => {
                                                                createForm.setFieldsValue({
                                                                    strategyCode: EXAMPLE_STRATEGIES[selectedExample].code
                                                                });
                                                                messageApi.success('代码已复制到输入框');
                                                            }}
                                                            aria-label="使用此代码"
                                                        >
                                                            <AppstoreAddOutlined/>
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                                <pre className={styles.exampleCode}>
                                                    <code>{EXAMPLE_STRATEGIES[selectedExample].code}</code>
                                                </pre>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Form.Item>
                            {/* 策略代码验证状态 */}
                            <Form.Item label="代码验证">
                                <div className={styles.validateSection}>
                                    {validating ? (
                                        <Spin tip="正在验证策略代码..."/>
                                    ) : validateResult ? (
                                        <div className={styles.validateResult}>
                                            {validateResult.valid ? (
                                                <Alert
                                                    type="success"
                                                    message="验证通过"
                                                    description={
                                                        validateResult.strategyClassName ?
                                                            `检测到策略类: ${validateResult.strategyClassName}` : undefined
                                                    }
                                                    showIcon
                                                />
                                            ) : (
                                                <Alert
                                                    type="error"
                                                    message="验证失败"
                                                    description={
                                                        <ul className={styles.errorList}>
                                                            {validateResult.errors?.map((err, idx) => (
                                                                <li key={idx}>{err}</li>
                                                            ))}
                                                        </ul>
                                                    }
                                                    showIcon
                                                />
                                            )}
                                            {validateResult.warnings && validateResult.warnings.length > 0 && (
                                                <Alert
                                                    type="warning"
                                                    message="建议"
                                                    description={
                                                        <ul className={styles.warningList}>
                                                            {validateResult.warnings.map((warn, idx) => (
                                                                <li key={idx}>{warn}</li>
                                                            ))}
                                                        </ul>
                                                    }
                                                    showIcon
                                                    style={{marginTop: 8}}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <span className={styles.validateHint}>点击"下一步"将自动验证策略代码</span>
                                    )}
                                </div>
                            </Form.Item>
                        </>
                    )}
                    {createStep === 2 && (
                        <>
                            <Form.Item name="description" label="描述">
                                <Input.TextArea rows={4} placeholder="策略描述信息"/>
                            </Form.Item>
                            <Form.Item name="isActive" label="启用状态" valuePropName="checked">
                                <Switch checkedChildren="启用" unCheckedChildren="禁用"/>
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
            <Modal
                title={taskRunMode === 'LIVE' ? '启动实盘任务' : '启动模拟盘任务'}
                open={taskModalVisible}
                onCancel={closeTaskModal}
                width={640}
                footer={(
                    <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}>
                        <Tooltip title="取消">
                            <Button shape="circle" icon={<CloseOutlined/>} onClick={closeTaskModal}
                                    disabled={launchingTask} aria-label="取消"/>
                        </Tooltip>
                        <Tooltip title={taskRunMode === 'LIVE' ? '启动实盘' : '启动模拟盘'}>
                            <Button type="primary" shape="circle"
                                    icon={taskRunMode === 'LIVE' ? <RocketOutlined/> : <PlayCircleOutlined/>}
                                    onClick={submitTaskLaunch}
                                    loading={launchingTask}
                                    aria-label={taskRunMode === 'LIVE' ? '启动实盘' : '启动模拟盘'}/>
                        </Tooltip>
                    </div>
                )}
            >
                <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                    {selectedTaskStrategy && (
                        <Alert
                            type="info"
                            showIcon
                            message={selectedTaskStrategy.title}
                            description={`将以 ${taskRunMode === 'LIVE' ? '实盘' : '模拟盘'} 模式创建任务并立即启动`}
                        />
                    )}
                    <Form form={taskForm} layout="vertical">
                        <Form.Item
                            name="apiKeyId"
                            label="API 连接"
                            rules={[{required: true, message: '请选择 API 连接'}]}
                        >
                            <Select
                                placeholder="选择交易账户连接"
                                options={apiConnections.map(connection => ({
                                    value: connection.id!,
                                    label: `${connection.name || '未命名连接'} · ${connection.exchange || 'Unknown'}`
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="freqtradeConfigId"
                            label="Freqtrade 配置"
                            rules={[{required: true, message: '请选择 Freqtrade 配置'}]}
                        >
                            <Select
                                placeholder="选择 Freqtrade 配置"
                                options={freqtradeConfigs.map(config => ({
                                    value: config.id,
                                    label: config.configName || config.name || `配置 ${config.id}`
                                }))}
                            />
                        </Form.Item>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                            <Form.Item
                                name="tradingPair"
                                label="交易对"
                                rules={[{required: true, message: '请输入交易对'}]}
                            >
                                <Input placeholder="例如 BTC/USDT"/>
                            </Form.Item>
                            <Form.Item
                                name="initialCapital"
                                label="初始资金 (USDT)"
                                rules={[{required: true, message: '请输入初始资金'}]}
                            >
                                <InputNumber style={{width: '100%'}} min={0} step={100}/>
                            </Form.Item>
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                            <Form.Item
                                name="stopLossPercent"
                                label="止损 (%)"
                                rules={[{required: true, message: '请输入止损百分比'}]}
                            >
                                <InputNumber style={{width: '100%'}} max={0} min={-100} step={1}/>
                            </Form.Item>
                            <Form.Item
                                name="takeProfitPercent"
                                label="止盈 (%)"
                                rules={[{required: true, message: '请输入止盈百分比'}]}
                            >
                                <InputNumber style={{width: '100%'}} min={0} max={1000} step={1}/>
                            </Form.Item>
                        </div>
                    </Form>
                    {apiConnections.length === 0 && (
                        <Alert type="warning" showIcon message="当前没有可用的 API 连接，请先在连接管理中启用账户。"/>
                    )}
                    {freqtradeConfigs.length === 0 && (
                        <Alert type="warning" showIcon message="当前没有可用的 Freqtrade 配置，请先完成配置。 "/>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default MyStrategyPage;
